/**
 * Kasa kayıt hatası bildirimi (Aşama 0 · ölçüm).
 *
 * Kasada "Ödeme Kaydı Hatası" ekranı her çıktığında bir olay sunucuya gönderilir
 * (gts_dotnetapi → dbo.PosSaveErrors). Süper Admin "Kasa Hataları" sayfası ve Grafana
 * bu kayıtlardan beslenir; sunucu her olay için satışın gerçekte kaydedilip
 * kaydedilmediğini (AtomicSaleIdempotency) kendisi kontrol eder.
 *
 * Kurallar:
 * - Asla kasayı bekletmez ve asla hata fırlatmaz (fire-and-forget).
 * - Gönderilemeyen olaylar tarayıcıda küçük bir kuyrukta (localStorage) bekler;
 *   60 sn'de bir, bağlantı geri gelince ve sayfa açılınca yeniden denenir.
 *   Aynı olay iki kez giderse sunucu EventId ile tekilleştirir.
 * - Oturum kapatılınca kuyruk SİLİNMEZ (ölçüm verisi kaybolmasın).
 */
import { mainApi } from '../config/apiClient';
import { APP_VERSION } from '../version';

export type PosErrorKind = 'zaman-asimi' | 'ag' | 'sunucu' | 'istemci';

/** 0 açık, 1 yeniden denemede kaydedildi, 2 kasiyer kapattı, 3 sayfa açılınca kurtarıldı */
export type PosClientOutcome = 0 | 1 | 2 | 3;

export interface PosSaveErrorInput {
  /** Kasada görülen hata metni (Error.message / sunucu Message) */
  errorMessage: string;
  /** Biliniyorsa sınıf; verilmezse mesajdan tahmin edilir */
  errorKind?: PosErrorKind;
  httpStatus?: number | null;
  /** Satış kaydı isteğinin işlem anahtarı (IdempotencyKey) */
  idempotencyKey?: string | null;
  /** ör. process-partial-with-invoice */
  endpoint?: string | null;
  branchId?: string | null;
  screenId?: number | null;
  /** restoran | hizli-satis | paket */
  screenMode?: 'restoran' | 'hizli-satis' | 'paket' | null;
  tableName?: string | null;
  /** nakit | pos | karma | acik-hesap | diger | online */
  paymentKind?: string | null;
  /** pavo_rest_p2p | pavo_p2p | pavopay | beko | ingenico | inpos | hugin | yok */
  fiscalDevice?: string | null;
  /** Yazarkasa parayı aldı / fiş kesti mi */
  fiscalDone?: boolean | null;
  /** Yazarkasa fiş/satış no */
  fiscalReference?: string | null;
  pavoRequestId?: string | null;
  amount?: number | null;
  /** İsteğin süresi (ms) */
  durationMs?: number | null;
  /** Kaçıncı deneme (1 = ilk) */
  attempt?: number;
  clientOutcome?: PosClientOutcome;
  /** Ad soyad — e-posta GÖNDERMEYİN */
  cashierName?: string | null;
}

interface PosSaveErrorEvent extends PosSaveErrorInput {
  eventId: string;
  errorKind: PosErrorKind;
  occurredAt: string;
  appVersion: string;
  attempt: number;
  clientOutcome: PosClientOutcome;
}

interface OutcomeUpdate {
  idempotencyKey: string;
  outcome: PosClientOutcome;
  at: string;
}

interface Outbox {
  events: PosSaveErrorEvent[];
  outcomes: OutcomeUpdate[];
}

const OUTBOX_KEY = 'gts_pos_telemetry_outbox';
const MAX_ITEMS = 200;
const FLUSH_INTERVAL_MS = 60_000;

const newId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch { /* yoksay */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

const readOutbox = (): Outbox => {
  try {
    const raw = localStorage.getItem(OUTBOX_KEY);
    if (!raw) return { events: [], outcomes: [] };
    const parsed = JSON.parse(raw);
    return {
      events: Array.isArray(parsed?.events) ? parsed.events : [],
      outcomes: Array.isArray(parsed?.outcomes) ? parsed.outcomes : [],
    };
  } catch {
    return { events: [], outcomes: [] };
  }
};

const writeOutbox = (box: Outbox): void => {
  try {
    const trimmed: Outbox = {
      events: box.events.slice(-MAX_ITEMS),
      outcomes: box.outcomes.slice(-MAX_ITEMS),
    };
    if (trimmed.events.length === 0 && trimmed.outcomes.length === 0) {
      localStorage.removeItem(OUTBOX_KEY);
    } else {
      localStorage.setItem(OUTBOX_KEY, JSON.stringify(trimmed));
    }
  } catch { /* depolama dolu / kapalı: ölçüm kaybı kabul */ }
};

/**
 * Hata metninden ve (varsa) HTTP durumundan hata türünü çıkarır.
 * axios: "timeout of 60000ms exceeded", "Network Error"; fetch: "Failed to fetch".
 */
export const classifyPosError = (
  message: string | null | undefined,
  httpStatus?: number | null,
): PosErrorKind => {
  const m = (message || '').toLowerCase();
  if (m.includes('timeout') || m.includes('zaman aşımı') || m.includes('econnaborted') || m.includes('aborted')) {
    return 'zaman-asimi';
  }
  if (m.includes('network error') || m.includes('failed to fetch') || m.includes('err_network')
      || m.includes('bağlantı') || m.includes('load failed')) {
    return 'ag';
  }
  if (typeof httpStatus === 'number' && httpStatus > 0) return 'sunucu';
  // Sunucu iş kuralı mesajları (Success=false) HTTP durumu bilinmese de sunucu tarafıdır
  if (m.includes('kaydedilmiş') || m.includes('bulunamadı') || m.includes('işlem başarısız')
      || m.includes('sql') || m.includes('deadlock') || m.includes('beklenmeyen')) {
    return 'sunucu';
  }
  return 'istemci';
};

let flushing = false;

const flush = async (): Promise<void> => {
  if (flushing) return;
  const box = readOutbox();
  if (box.events.length === 0 && box.outcomes.length === 0) return;
  flushing = true;
  try {
    if (box.events.length > 0) {
      const batch = box.events.slice(0, 50);
      await mainApi.post('/api/pos-telemetry/save-errors', { events: batch }, { timeout: 15_000 });
      const sent = new Set(batch.map((e) => e.eventId));
      const now = readOutbox();
      now.events = now.events.filter((e) => !sent.has(e.eventId));
      writeOutbox(now);
    }
    const afterEvents = readOutbox();
    if (afterEvents.outcomes.length > 0) {
      const batch = afterEvents.outcomes.slice(0, 50);
      await mainApi.post('/api/pos-telemetry/save-outcomes', { outcomes: batch }, { timeout: 15_000 });
      const sentKeys = new Set(batch.map((o) => `${o.idempotencyKey}|${o.at}`));
      const now = readOutbox();
      now.outcomes = now.outcomes.filter((o) => !sentKeys.has(`${o.idempotencyKey}|${o.at}`));
      writeOutbox(now);
    }
  } catch {
    // Sonraki turda tekrar denenir
  } finally {
    flushing = false;
  }
};

/**
 * Kasada görülen bir kayıt hatasını bildirir. Olay numarasını döndürür.
 * Hiçbir zaman hata fırlatmaz, kasayı bekletmez.
 */
export const reportPosSaveError = (input: PosSaveErrorInput): string => {
  const eventId = newId();
  try {
    const message = (input.errorMessage || '').slice(0, 1000);
    const event: PosSaveErrorEvent = {
      ...input,
      eventId,
      errorMessage: message,
      errorKind: input.errorKind ?? classifyPosError(message, input.httpStatus),
      occurredAt: new Date().toISOString(),
      appVersion: APP_VERSION,
      attempt: input.attempt ?? 1,
      clientOutcome: input.clientOutcome ?? 0,
    };
    const box = readOutbox();
    box.events.push(event);
    writeOutbox(box);
    void flush();
  } catch { /* ölçüm asla kasayı bozmaz */ }
  return eventId;
};

/**
 * Daha önce hata bildirilen bir satışın sonucunu bildirir (aynı işlem anahtarı):
 * 1 = yeniden denemede kaydedildi, 2 = kasiyer kapattı, 3 = sayfa açılınca kurtarıldı.
 */
export const reportPosSaveOutcome = (
  idempotencyKey: string | null | undefined,
  outcome: PosClientOutcome,
): void => {
  if (!idempotencyKey) return;
  try {
    const box = readOutbox();
    box.outcomes.push({ idempotencyKey, outcome, at: new Date().toISOString() });
    writeOutbox(box);
    void flush();
  } catch { /* yoksay */ }
};

// Kuyruğu periyodik ve bağlantı geri gelince boşalt
let started = false;
export const startPosTelemetry = (): void => {
  if (started || typeof window === 'undefined') return;
  started = true;
  try {
    window.addEventListener('online', () => { void flush(); });
    window.setInterval(() => { void flush(); }, FLUSH_INTERVAL_MS);
    window.setTimeout(() => { void flush(); }, 5_000);
  } catch { /* yoksay */ }
};

startPosTelemetry();
